import { JSX } from "preact"
import { useEffect, useState } from "preact/hooks"
import FilterSidebar from "@/components/FilterSidebar/FilterSidebar"
import { useNostoAppState } from "@nosto/search-js/preact/hooks"
import styles from "./ContentWrapper.module.css"
import { cl } from "@nosto/search-js/utils"
import { isRenderableProductHit } from "@/utils/isRenderableProductHit"
import type { Product } from "@/types"
import { GridResultContext } from "@/contexts/GridResultContext"
import { useStyleFamilyCatalog } from "@/contexts/StyleFamilyCatalogContext"

export type ContentChildrenProps = { loading: boolean; foundProducts: boolean }

type ContentWrapperProps = {
  type: string
  children: (props: ContentChildrenProps) => JSX.Element | null
}

const POLL_INTERVAL_MS = 200
const MAX_POLL_ATTEMPTS = 50 // ~10s of polling

/**
 * Watches the rendered product grid for the case where Nosto returns hits but
 * every `<nosto-dynamic-card>` resolves to an empty card (e.g. the product is
 * not published to the current market). When all settled cards are empty,
 * returns true so the caller can collapse to a no-results state instead of
 * showing a count + pagination over an empty grid.
 *
 * Uses a polling loop rather than a `MutationObserver` on `document.body`,
 * because storefront pages mutate constantly (cookie banners, third-party
 * scripts) which would keep resetting a debounced observer.
 */
function useAllCardsEmpty(querySignature: string, foundProducts: boolean) {
  const [allEmpty, setAllEmpty] = useState(false)

  useEffect(() => {
    setAllEmpty(false)
    if (!foundProducts) return

    let cancelled = false
    let timer: number | undefined
    let attempts = 0

    const evaluate = () => {
      if (cancelled) return
      attempts++

      const grid = document.querySelector("[data-nosto-products-grid]")
      const cards = grid ? Array.from(grid.querySelectorAll("nosto-dynamic-card")) : []
      const settled =
        cards.length > 0 &&
        cards.every(
          c =>
            c.querySelector("[data-nosto-empty-card]") != null ||
            c.hasAttribute("empty") ||
            c.hasAttribute("error") ||
            c.innerHTML.trim().length > 0
        )

      if (settled) {
        const everyEmpty = cards.every(c => c.querySelector("[data-nosto-empty-card]") != null)
        if (everyEmpty) {
          setAllEmpty(true)
        }
        return // settled — stop polling either way
      }

      if (attempts < MAX_POLL_ATTEMPTS) {
        timer = window.setTimeout(evaluate, POLL_INTERVAL_MS)
      }
    }

    timer = window.setTimeout(evaluate, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      if (timer != null) window.clearTimeout(timer)
    }
  }, [querySignature, foundProducts])

  return allEmpty
}

/**
 * ContentWrapper takes a function to render non-empty results in the content area.
 * On loading nothing is shown and for empty results the NoResults component is used.
 *
 * `foundProducts` is derived from the number of *renderable* hits, not the raw
 * `response.products.total`. Nosto can return a non-zero total while every hit
 * is filtered out (e.g. unavailable products), and we should treat that as an
 * empty result set so the no-results state shows and BottomToolbar/Pagination
 * are hidden. We also collapse to no-results when every rendered Nosto dynamic
 * card resolves empty (e.g. products that are not available in the active
 * Shopify market).
 */
function ContentWrapper({ type, children }: ContentWrapperProps) {
  const { foundProducts, loading, initialized, querySignature } = useNostoAppState(state => {
    const hits = (state.response.products?.hits ?? []) as Product[]
    const renderable = hits.some(isRenderableProductHit)
    return {
      foundProducts: renderable,
      loading: state.loading,
      initialized: state.initialized,
      querySignature: `${state.response.query ?? ""}|${state.query?.products?.from ?? 0}`
    }
  })

  const catalog = useStyleFamilyCatalog()
  // The catalog runs its own chunked refetch *after* Nosto's main response
  // settles. When Nosto's `loading` flips back to `false` but the catalog is
  // still mid-fetch (or about to start one because the query signature just
  // changed), `Category.tsx` would briefly switch to `<NoResults />` before
  // skeletons came back — a visible "skeleton -> empty flash -> skeleton" gap
  // . Treat the catalog as still-loading from the caller's
  // perspective so the grid stays in skeleton mode through that handoff.
  const catalogLoading = catalog?.status === "loading"
  const effectiveLoading = loading || catalogLoading

  // After the catalog applies its per-market availability filter, every hit
  // can be unbuyable in the current Shopify market (en-ca / fr-ca). Treat the
  // ready-but-empty catalog as no-results so we render `<NoResults />` instead
  // of an empty grid with a non-zero Nosto count + bogus pagination.
  const catalogResolvedEmpty = catalog?.status === "ready" && (catalog.dedupedHits?.length ?? 0) === 0

  const allCardsEmpty = useAllCardsEmpty(querySignature, foundProducts && !effectiveLoading)
  const effectiveFoundProducts = foundProducts && !catalogResolvedEmpty && !allCardsEmpty

  if (!initialized) {
    return null
  }

  // FilterSidebar is mounted unconditionally. Gating it on `effectiveFoundProducts`
  // unmounted the dialog whenever Nosto's response transiently looked empty
  // (loading flips, polled `allCardsEmpty` flicker), which replayed the
  // `dialog-slide-in` animation and re-called `dialog.showModal()` — visible as
  // the panel closing and reopening on filter selection.
  // FilterSidebar's own `facets?.length === 0` guard handles the no-results case.
  return (
    <GridResultContext.Provider value={{ hasVisibleResults: effectiveFoundProducts, loading: effectiveLoading }}>
      <div className={styles.wrapper} data-nosto-element={type}>
        <FilterSidebar />
        <div className={cl(styles.container, effectiveLoading && styles.loading)}>
          {children({ loading: effectiveLoading, foundProducts: effectiveFoundProducts })}
        </div>
      </div>
    </GridResultContext.Provider>
  )
}

export function wrapContent(type: string, Component: ContentWrapperProps["children"]) {
  // eslint-disable-next-line react/display-name
  return () => <ContentWrapper type={type}>{Component}</ContentWrapper>
}
